/**
 * Unified Sync Service
 * 
 * Handles synchronization of offline data when coming back online.
 * Uses IndexedDB (via offlineDatabase) as primary storage with 
 * localStorage fallback for lightweight operations.
 * 
 * Features:
 * - Automatic sync on coming online
 * - Periodic background sync
 * - Retry logic with exponential backoff
 * - Priority-based queue processing
 */

import { offlineDatabase, SyncQueueItem } from './offlineDatabase';
import { 
    saveChatSession, 
    updateChatSession, 
    deleteChatSession,
    updateUser,
    saveGeneratedMaterial
} from './firebaseService';

const MAX_RETRY_ATTEMPTS = 3;
const SYNC_INTERVAL = 30000; // 30 seconds

let syncIntervalId: ReturnType<typeof setInterval> | null = null;
let isSyncing = false;
let lastSyncTime = 0;
let lastNoActionsLog = 0; // Track last "no actions" log to reduce noise

// ==================== Service Control ====================

/**
 * Start the sync service
 */
export const startSyncService = (): void => {
    if (syncIntervalId) return;
    
    // Sync immediately
    syncOfflineData();

    // Set up periodic sync
    syncIntervalId = setInterval(() => {
        if (navigator.onLine) {
            syncOfflineData();
        }
    }, SYNC_INTERVAL);

    // Listen for online event
    window.addEventListener('online', handleOnline);
};

/**
 * Stop the sync service
 */
export const stopSyncService = (): void => {
    if (syncIntervalId) {
        clearInterval(syncIntervalId);
        syncIntervalId = null;
    }
    window.removeEventListener('online', handleOnline);
};

/**
 * Handle coming back online
 */
const handleOnline = (): void => {
    syncOfflineData();
};

// ==================== Sync Operations ====================

/**
 * Sync all queued offline actions
 */
export const syncOfflineData = async (): Promise<void> => {
    if (isSyncing || !navigator.onLine) return;

    // Get queue from IndexedDB
    const queue = await offlineDatabase.getSyncQueue();
    if (queue.length === 0) return;

    isSyncing = true;
    console.log(`[Sync] Starting sync of ${queue.length} actions`);

    let successCount = 0;
    let failureCount = 0;

    // Sort by priority (higher first) and timestamp (older first)
    const sortedQueue = [...queue].sort((a, b) => {
        if (a.priority !== b.priority) return b.priority - a.priority;
        return a.timestamp - b.timestamp;
    });

    for (const action of sortedQueue) {
        try {
            // Skip if exceeded retry attempts
            if (action.retryCount >= MAX_RETRY_ATTEMPTS) {
                console.warn(`[Sync] Skipping action ${action.id} - max retries exceeded`);
                await offlineDatabase.removeFromSyncQueue(action.id);
                failureCount++;
                continue;
            }

            // Process the action
            await processOfflineAction(action);
            
            // Remove from queue on success
            await offlineDatabase.removeFromSyncQueue(action.id);
            successCount++;
            console.log(`[Sync] Successfully synced action: ${action.type} - ${action.action}`);

        } catch (error) {
            console.error(`[Sync] Failed to sync action ${action.id}:`, error);
            await offlineDatabase.incrementSyncRetry(action.id);
            failureCount++;
        }
    }

    isSyncing = false;
    lastSyncTime = Date.now();
    
    // Also update localStorage for backward compatibility
    localStorage.setItem('luca_last_sync', lastSyncTime.toString());

    console.log(`[Sync] Completed - Success: ${successCount}, Failed: ${failureCount}`);

    // Notify user if there were failures
    if (failureCount > 0 && successCount === 0) {
        console.warn('[Sync] All sync attempts failed');
    }
};

/**
 * Process a single offline action
 */
const processOfflineAction = async (item: SyncQueueItem): Promise<void> => {
    const { type, action, data } = item;

    switch (type) {
        case 'session':
            if (action === 'create' || action === 'update') {
                if (data.sessionId && data.updates) {
                    // Update with specific updates
                    await updateChatSession(data.sessionId, data.updates);
                } else {
                    // Full session save
                    await saveChatSession(data);
                }
            } else if (action === 'delete') {
                await deleteChatSession(data.sessionId || data.id);
            }
            break;

        case 'user':
            if (action === 'update' && data.userId && data.userUpdates) {
                await updateUser(data.userId, data.userUpdates);
            } else if (data.userId && data.updates) {
                await updateUser(data.userId, data.updates);
            }
            break;

        case 'message':
            // Messages are part of sessions
            if (data.session) {
                await saveChatSession(data.session);
            }
            break;

        case 'material':
            if (action === 'create' && data.material) {
                await saveGeneratedMaterial(data.material);
            }
            break;

        default:
            console.warn(`[Sync] Unknown action type: ${type}`);
    }
};

// ==================== Public API ====================

/**
 * Force sync now (manual trigger)
 */
export const forceSyncNow = async (): Promise<{ success: boolean; message: string }> => {
    if (!navigator.onLine) {
        return {
            success: false,
            message: 'Cannot sync while offline'
        };
    }

    const queue = await offlineDatabase.getSyncQueue();
    
    if (queue.length === 0) {
        return {
            success: true,
            message: 'Nothing to sync'
        };
    }

    try {
        await syncOfflineData();
        return {
            success: true,
            message: `Synced ${queue.length} action(s)`
        };
    } catch (error) {
        return {
            success: false,
            message: 'Sync failed. Will retry automatically.'
        };
    }
};

/**
 * Get sync status
 */
export const getSyncStatus = async (): Promise<{
    isOnline: boolean;
    isSyncing: boolean;
    pendingActions: number;
    lastSyncTime: number | null;
}> => {
    const queue = await offlineDatabase.getSyncQueue();
    const storedLastSync = localStorage.getItem('luca_last_sync');
    
    return {
        isOnline: navigator.onLine,
        isSyncing,
        pendingActions: queue.length,
        lastSyncTime: lastSyncTime || (storedLastSync ? parseInt(storedLastSync, 10) : null)
    };
};

/**
 * Clear all pending sync actions (use with caution!)
 */
export const clearPendingSync = async (): Promise<void> => {
    await offlineDatabase.clearSyncQueue();
    console.log('[Sync] Cleared all pending sync actions');
};

/**
 * Queue an action for offline sync
 * Convenience wrapper around offlineDatabase.addToSyncQueue
 */
export const queueForSync = async (
    type: 'session' | 'user' | 'message' | 'material',
    action: 'create' | 'update' | 'delete',
    data: any,
    priority: number = 3
): Promise<void> => {
    await offlineDatabase.addToSyncQueue({
        type,
        action,
        data,
        priority
    });
    
    // Try to sync immediately if online
    if (navigator.onLine && !isSyncing) {
        // Small delay to batch rapid changes
        setTimeout(() => syncOfflineData(), 1000);
    }
};
